import type { MeasurementQuality, ReadingSample } from '../types/domain'

export const BLE_UUIDS = {
  service: import.meta.env.VITE_BLE_SERVICE_UUID ?? '0000ff00-0000-1000-8000-00805f9b34fb',
  info: import.meta.env.VITE_BLE_INFO_UUID ?? '0000ff01-0000-1000-8000-00805f9b34fb',
  data: import.meta.env.VITE_BLE_DATA_UUID ?? '0000ff02-0000-1000-8000-00805f9b34fb',
  control: import.meta.env.VITE_BLE_CONTROL_UUID ?? '0000ff03-0000-1000-8000-00805f9b34fb',
} as const

export const SENSOR_CHANNELS = {
  0: { side: 'A', label: 'Грань A' },
  1: { side: 'B', label: 'Грань B' },
} as const

export type SensorSide = typeof SENSOR_CHANNELS[keyof typeof SENSOR_CHANNELS]['side']
export type ControlCommand = 'READ' | 'ACK' | 'CLEAR'

export const PROTOCOL_VERSION = 1
export const NOTIFICATION_FRAME_BYTES = 20
export const LEGACY_RECORD_BYTES = 16

const MAGIC = 0x4e
const FRAME_BODY_BYTES = NOTIFICATION_FRAME_BYTES - 2

const frameTypes = {
  START: 0x01,
  RECORD: 0x02,
  END: 0x03,
  ERROR: 0x7f,
} as const

const commandCodes: Record<ControlCommand, number> = {
  READ: 0x01,
  ACK: 0x02,
  CLEAR: 0x03,
}

export type SensorFrame =
  | { type: 'START'; transferId: number; totalRecords: number; sampleIntervalMs: number; channelCount: number }
  | { type: 'RECORD'; sample: ReadingSample }
  | { type: 'END'; transferId: number; sentRecords: number; lastSequenceNo: number; status: number }
  | { type: 'ERROR'; code: number; message: string }

export interface DecodedControlCommand {
  command: ControlCommand
  transferId: number
  lastSequenceNo: number
}

export function channelSide(channel: number): SensorSide {
  const definition = SENSOR_CHANNELS[channel as keyof typeof SENSOR_CHANNELS]
  if (!definition) throw new Error(`Неизвестный канал датчика: ${channel}`)
  return definition.side
}

/**
 * Целевой потоковый формат протокола v1. Один GATT notification равен одному
 * 20-байтному фрейму и укладывается в минимальный ATT MTU без фрагментации.
 */
export function decodeNotificationFrame(data: DataView): SensorFrame {
  if (data.byteLength !== NOTIFICATION_FRAME_BYTES) {
    throw new Error(`Размер BLE-фрейма ${data.byteLength}, ожидается ${NOTIFICATION_FRAME_BYTES}`)
  }
  if (data.getUint8(0) !== MAGIC) throw new Error('Некорректная сигнатура BLE-фрейма')
  const version = data.getUint8(1)
  if (version !== PROTOCOL_VERSION) throw new Error(`Версия BLE-протокола ${version} не поддерживается`)
  assertCrc(data)

  const frameType = data.getUint8(2)
  if (frameType === frameTypes.START) {
    const channelCount = data.getUint8(16)
    if (channelCount !== 2) throw new Error(`Датчик объявил ${channelCount} канал(а), ожидается 2 (A и B)`)
    return {
      type: 'START',
      transferId: data.getUint32(4, true),
      totalRecords: data.getUint32(8, true),
      sampleIntervalMs: data.getUint32(12, true),
      channelCount,
    }
  }
  if (frameType === frameTypes.RECORD) {
    const sequenceNo = data.getUint32(4, true)
    const epochSeconds = data.getUint32(8, true)
    const channel = data.getUint8(12)
    const quality = decodeQuality(data.getUint8(13))
    const rawValue = data.getFloat32(14, true)
    channelSide(channel)
    assertRawValue(rawValue, sequenceNo)
    return {
      type: 'RECORD',
      sample: {
        sequenceNo,
        channel,
        measuredAt: new Date(epochSeconds * 1000).toISOString(),
        rawValue,
        quality,
      },
    }
  }
  if (frameType === frameTypes.END) {
    return {
      type: 'END',
      transferId: data.getUint32(4, true),
      sentRecords: data.getUint32(8, true),
      lastSequenceNo: data.getUint32(12, true),
      status: data.getUint8(16),
    }
  }
  if (frameType === frameTypes.ERROR) {
    const messageBytes = new Uint8Array(data.buffer, data.byteOffset + 5, 13)
    return {
      type: 'ERROR',
      code: data.getUint8(4),
      message: new TextDecoder().decode(messageBytes).replace(/\0+$/, ''),
    }
  }
  throw new Error(`Неизвестный тип BLE-фрейма: ${frameType}`)
}

/**
 * Совместимый однократный формат: sequence u32, unix time u32, raw f32,
 * channel u8 (0=A, 1=B), quality u8, reserved u16.
 */
export function decodeRecords(data: DataView): ReadingSample[] {
  if (data.byteLength % LEGACY_RECORD_BYTES !== 0) {
    throw new Error(`Размер BLE-пакета ${data.byteLength} не кратен ${LEGACY_RECORD_BYTES}`)
  }
  const records: ReadingSample[] = []
  for (let offset = 0; offset < data.byteLength; offset += LEGACY_RECORD_BYTES) {
    const sequenceNo = data.getUint32(offset, true)
    const epochSeconds = data.getUint32(offset + 4, true)
    const rawValue = data.getFloat32(offset + 8, true)
    const channel = data.getUint8(offset + 12)
    const quality = decodeQuality(data.getUint8(offset + 13))
    channelSide(channel)
    assertRawValue(rawValue, sequenceNo)
    records.push({
      sequenceNo,
      channel,
      measuredAt: new Date(epochSeconds * 1000).toISOString(),
      rawValue,
      quality,
    })
  }
  return records
}

export function encodeControlCommand(command: ControlCommand, transferId = 0, lastSequenceNo = 0): Uint8Array {
  const bytes = new Uint8Array(14)
  const view = new DataView(bytes.buffer)
  view.setUint8(0, MAGIC)
  view.setUint8(1, PROTOCOL_VERSION)
  view.setUint8(2, commandCodes[command])
  view.setUint8(3, 0)
  view.setUint32(4, transferId, true)
  view.setUint32(8, lastSequenceNo, true)
  view.setUint16(12, crc16(bytes.subarray(0, 12)), true)
  return bytes
}

export function decodeControlCommand(data: DataView): DecodedControlCommand {
  if (data.byteLength !== 14) throw new Error(`Размер CONTROL-команды ${data.byteLength}, ожидается 14`)
  if (data.getUint8(0) !== MAGIC) throw new Error('Некорректная сигнатура CONTROL-команды')
  if (data.getUint8(1) !== PROTOCOL_VERSION) throw new Error('Версия CONTROL-команды не поддерживается')
  const expected = data.getUint16(12, true)
  const bytes = new Uint8Array(data.buffer, data.byteOffset, 12)
  if (crc16(bytes) !== expected) throw new Error('Ошибка CRC CONTROL-команды')
  const code = data.getUint8(2)
  const command = Object.entries(commandCodes).find(([, value]) => value === code)?.[0] as ControlCommand | undefined
  if (!command) throw new Error(`Неизвестная CONTROL-команда: ${code}`)
  return {
    command,
    transferId: data.getUint32(4, true),
    lastSequenceNo: data.getUint32(8, true),
  }
}

export function encodeStartFrame(transferId: number, totalRecords: number, sampleIntervalMs: number): Uint8Array {
  return encodeFrame(frameTypes.START, view => {
    view.setUint32(4, transferId, true)
    view.setUint32(8, totalRecords, true)
    view.setUint32(12, sampleIntervalMs, true)
    view.setUint8(16, 2)
  })
}

export function encodeRecordFrame(sample: ReadingSample): Uint8Array {
  channelSide(sample.channel)
  assertRawValue(sample.rawValue, sample.sequenceNo)
  const epochSeconds = Math.floor(new Date(sample.measuredAt).getTime() / 1000)
  if (!Number.isFinite(epochSeconds) || epochSeconds < 0 || epochSeconds > 0xffffffff) {
    throw new Error(`Некорректное время в записи ${sample.sequenceNo}`)
  }
  return encodeFrame(frameTypes.RECORD, view => {
    view.setUint32(4, sample.sequenceNo, true)
    view.setUint32(8, epochSeconds, true)
    view.setUint8(12, sample.channel)
    view.setUint8(13, encodeQuality(sample.quality))
    view.setFloat32(14, sample.rawValue, true)
  })
}

export function encodeEndFrame(transferId: number, sentRecords: number, lastSequenceNo: number, status = 0): Uint8Array {
  return encodeFrame(frameTypes.END, view => {
    view.setUint32(4, transferId, true)
    view.setUint32(8, sentRecords, true)
    view.setUint32(12, lastSequenceNo, true)
    view.setUint8(16, status)
  })
}

export function encodeErrorFrame(code: number, message: string): Uint8Array {
  return encodeFrame(frameTypes.ERROR, (view, bytes) => {
    view.setUint8(4, code)
    bytes.set(new TextEncoder().encode(message).subarray(0, 13), 5)
  })
}

/** Команды для старой PoC-прошивки без control characteristic. */
export function encodeLegacyCommand(command: 'ACK' | 'CLEAR'): Uint8Array {
  return new TextEncoder().encode(command === 'ACK' ? 'ACK\n' : 'CLEAR\n')
}

export function crc16(bytes: Uint8Array): number {
  let crc = 0xffff
  for (const byte of bytes) {
    crc ^= byte << 8
    for (let bit = 0; bit < 8; bit++) {
      crc = (crc & 0x8000) !== 0 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff
    }
  }
  return crc
}

function assertCrc(data: DataView) {
  const body = new Uint8Array(data.buffer, data.byteOffset, FRAME_BODY_BYTES)
  const expected = data.getUint16(FRAME_BODY_BYTES, true)
  const actual = crc16(body)
  if (actual !== expected) throw new Error(`Ошибка CRC BLE-фрейма: ${actual}, ожидается ${expected}`)
}

function decodeQuality(code: number): MeasurementQuality {
  if (code === 0) return 'GOOD'
  if (code === 1) return 'SUSPECT'
  if (code === 2) return 'INVALID'
  throw new Error(`Неизвестный код качества измерения: ${code}`)
}

function encodeQuality(quality: MeasurementQuality): number {
  return quality === 'GOOD' ? 0 : quality === 'SUSPECT' ? 1 : 2
}

function encodeFrame(type: number, writePayload: (view: DataView, bytes: Uint8Array) => void): Uint8Array {
  const bytes = new Uint8Array(NOTIFICATION_FRAME_BYTES)
  const view = new DataView(bytes.buffer)
  view.setUint8(0, MAGIC)
  view.setUint8(1, PROTOCOL_VERSION)
  view.setUint8(2, type)
  writePayload(view, bytes)
  view.setUint16(FRAME_BODY_BYTES, crc16(bytes.subarray(0, FRAME_BODY_BYTES)), true)
  return bytes
}

function assertRawValue(rawValue: number, sequenceNo: number) {
  if (!Number.isFinite(rawValue)) throw new Error(`Некорректное значение в записи ${sequenceNo}`)
}
